import React from 'react';

// ============================================================
// Interactive typology wheel — circular segmented view
// ============================================================

export function TypologyWheel({ title, subtitle, typology, selected, onToggle, onToggleMain, orgs, matchSub }) {
  const size = 640;
  const cx = size / 2, cy = size / 2;
  const outerR = 240;   // outer edge of main band
  const bandW = 62;     // width of main category band
  const subOuterR = outerR - bandW - 6;
  const subInnerR = 90;
  const centerR = 78;
  const [hoverInfo, setHoverInfo] = React.useState(null); // { x, y, label, count }

  const nCats = typology.length;
  const segAngle = (Math.PI * 2) / nCats;

  // Count orgs per sub
  const subCounts = {};
  typology.forEach(cat => cat.sub.forEach(sub => {
    subCounts[sub] = orgs.filter(o => matchSub(o, sub)).length;
  }));
  const mainCounts = {};
  typology.forEach(cat => {
    mainCounts[cat.name] = orgs.filter(o => cat.sub.some(sub => matchSub(o, sub))).length;
  });

  // Arc path helper
  const arcPath = (r1, r2, a1, a2) => {
    const x1 = cx + r2 * Math.cos(a1), y1 = cy + r2 * Math.sin(a1);
    const x2 = cx + r2 * Math.cos(a2), y2 = cy + r2 * Math.sin(a2);
    const x3 = cx + r1 * Math.cos(a2), y3 = cy + r1 * Math.sin(a2);
    const x4 = cx + r1 * Math.cos(a1), y4 = cy + r1 * Math.sin(a1);
    const large = (a2 - a1) > Math.PI ? 1 : 0;
    return `M ${x1} ${y1} A ${r2} ${r2} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${r1} ${r1} 0 ${large} 0 ${x4} ${y4} Z`;
  };

  const polar = (r, a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];

  // Touch reveal: on a tap (pointerup, so a scroll that starts on a segment
  // fires pointercancel and is ignored), show the full label as a *sticky*
  // tooltip that stays until another segment, the centre, or empty space is
  // tapped. Desktop pointers fall through to the existing hover handlers.
  const pointerReveal = (e, label, count) => {
    if (e.pointerType !== 'touch') return;
    const wrap = e.currentTarget.ownerSVGElement.parentElement.getBoundingClientRect();
    const x = e.clientX - wrap.left, y = e.clientY - wrap.top;
    setHoverInfo({ x: Math.max(8, x - 55), y: Math.max(6, y - 52), label, count, sticky: true });
  };

  return (
    <div className="typology-wheel-wrap" style={{ position: 'relative' }}>
      <div className="wheel-head">
        <h3>{title}</h3>
        <p className="muted">{subtitle}</p>
      </div>
      <svg viewBox={`0 0 ${size} ${size}`} className="typology-wheel"
        onPointerDown={(e) => { if (e.pointerType === 'touch' && e.target === e.currentTarget) setHoverInfo(null); }}
        onMouseLeave={() => setHoverInfo(h => h && h.sticky ? h : null)}>
        {typology.map((cat, i) => {
          const a1 = -Math.PI / 2 + i * segAngle;
          const a2 = -Math.PI / 2 + (i + 1) * segAngle;
          const mid = (a1 + a2) / 2;
          const isMainSelected = selected.has(cat.name) || cat.sub.every(s => selected.has(s));
          const subSelCount = cat.sub.filter(s => selected.has(s)).length;
          const isPartial = subSelCount > 0 && !isMainSelected;
          const count = mainCounts[cat.name];

          // Sub-segments within this category's slice
          const nSubs = cat.sub.length;
          const subAngle = segAngle / nSubs;

          return (
            <g key={cat.name}>
              {/* Sub-category segments */}
              {cat.sub.map((sub, j) => {
                const sa1 = a1 + j * subAngle;
                const sa2 = a1 + (j + 1) * subAngle;
                const smid = (sa1 + sa2) / 2;
                const isSel = selected.has(sub);
                const count = subCounts[sub] || 0;
                const opacity = count === 0 ? 0.25 : (isSel ? 1 : 0.55);
                // Arc length at the label radius -> max chars that fit
                const arcLen = ((subInnerR + subOuterR) / 2) * (sa2 - sa1);
                return (
                  <g key={sub} className="wheel-sub"
                     onClick={(e) => { e.stopPropagation(); onToggle(sub); }}
                     onPointerUp={(e) => pointerReveal(e, sub, count)}
                     onMouseEnter={(e) => {
                       const wrap = e.currentTarget.ownerSVGElement.parentElement.getBoundingClientRect();
                       setHoverInfo({
                         x: e.clientX - wrap.left + 12, y: e.clientY - wrap.top + 12,
                         label: sub, count
                       });
                     }}
                     onMouseMove={(e) => {
                       const wrap = e.currentTarget.ownerSVGElement.parentElement.getBoundingClientRect();
                       setHoverInfo(h => h && !h.sticky ? { ...h, x: e.clientX - wrap.left + 12, y: e.clientY - wrap.top + 12 } : h);
                     }}
                     onMouseLeave={() => setHoverInfo(h => h && h.sticky ? h : null)}>
                    <path d={arcPath(subInnerR, subOuterR, sa1 + 0.004, sa2 - 0.004)}
                          fill={cat.color}
                          fillOpacity={opacity}
                          stroke={isSel ? '#1A1A1A' : 'rgba(255,255,255,0.9)'}
                          strokeWidth={isSel ? 1.6 : 1}
                          className={`wheel-sub-path ${isSel ? 'is-sel' : ''}`}
                          style={{ cursor: 'pointer' }}>
                      <title>{sub} ({count})</title>
                    </path>
                    {/* Sub label, rotated to fit, smart-truncated */}
                    <SubLabel cx={cx} cy={cy} r={(subInnerR + subOuterR)/2} angle={smid} text={sub} arcLen={arcLen}/>
                  </g>
                );
              })}

              {/* Main category band */}
              <path d={arcPath(outerR - bandW, outerR, a1 + 0.004, a2 - 0.004)}
                    fill={cat.color}
                    fillOpacity={isMainSelected ? 1 : 0.78}
                    stroke={isMainSelected ? '#1A1A1A' : 'rgba(255,255,255,0.9)'}
                    strokeWidth={isMainSelected ? 2 : 1}
                    onClick={() => onToggleMain(cat)}
                    onPointerUp={(e) => pointerReveal(e, cat.name + ' (all)', count)}
                    onMouseEnter={(e) => {
                      const wrap = e.currentTarget.ownerSVGElement.parentElement.getBoundingClientRect();
                      setHoverInfo({ x: e.clientX - wrap.left + 12, y: e.clientY - wrap.top + 12, label: cat.name + ' (all)', count });
                    }}
                    onMouseMove={(e) => {
                      const wrap = e.currentTarget.ownerSVGElement.parentElement.getBoundingClientRect();
                      setHoverInfo(h => h && !h.sticky ? { ...h, x: e.clientX - wrap.left + 12, y: e.clientY - wrap.top + 12 } : h);
                    }}
                    onMouseLeave={() => setHoverInfo(h => h && h.sticky ? h : null)}
                    className={`wheel-main-path ${isMainSelected ? 'is-sel' : ''}`}
                    style={{ cursor: 'pointer' }}>
                <title>{cat.name} — click to toggle all</title>
              </path>
              {isPartial && <path d={arcPath(outerR - bandW, outerR, a1 + 0.004, a2 - 0.004)}
                    fill="none" stroke="#1A1A1A" strokeWidth="1.5" strokeDasharray="2 3" pointerEvents="none"/>}

              {/* Main label (curved) */}
              <MainLabel cx={cx} cy={cy} r={outerR - bandW/2} a1={a1} a2={a2} text={cat.name} count={count} index={i}/>
            </g>
          );
        })}

        {/* Center */}
        <circle cx={cx} cy={cy} r={centerR} fill="#1A1A1A"
          onPointerUp={(e) => { if (e.pointerType === 'touch') setHoverInfo(null); }}/>
        <text x={cx} y={cy - 6} textAnchor="middle" className="wheel-center-label">
          {title.includes('Activity') ? 'ACTIVITY' : 'THEMATIC'}
        </text>
        <text x={cx} y={cy + 10} textAnchor="middle" className="wheel-center-sub">
          based typology
        </text>
        {selected.size > 0 && <text x={cx} y={cy + 28} textAnchor="middle" className="wheel-center-count">
          {selected.size} selected
        </text>}
      </svg>
      {hoverInfo && (
        <div className="wheel-tooltip" style={{ left: hoverInfo.x, top: hoverInfo.y }}>
          <div className="wheel-tooltip-label">{hoverInfo.label}</div>
          <div className="wheel-tooltip-count">{hoverInfo.count} org{hoverInfo.count === 1 ? '' : 's'}</div>
        </div>
      )}
    </div>
  );
}

// Smart truncation: word-boundary fit, no ellipsis. Returns longest prefix
// (whole words) that fits inside `maxChars`. Falls back to first word slice.
export function smartTruncate(text, maxChars) {
  if (!text || text.length <= maxChars) return text;
  const words = text.split(/\s+/);
  let acc = '';
  for (const w of words) {
    const candidate = acc ? `${acc} ${w}` : w;
    if (candidate.length > maxChars) break;
    acc = candidate;
  }
  if (!acc) return words[0].slice(0, Math.max(1, maxChars));
  return acc;
}

export function SubLabel({ cx, cy, r, angle, text, arcLen }) {
  const [x, y] = [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
  let deg = angle * 180 / Math.PI;
  if (deg > 90 || deg < -90) { deg += 180; }
  // 5.4 px per char @ 10.5px font size; clamp 6..30
  const maxChars = Math.max(6, Math.min(30, Math.floor((arcLen || 80) / 5.4)));
  const short = smartTruncate(text, maxChars);
  return (
    <text x={x} y={y} textAnchor="middle" dominantBaseline="middle"
          transform={`rotate(${deg}, ${x}, ${y})`}
          className="wheel-sub-label" pointerEvents="none">
      {short}
    </text>
  );
}

// Split an (uppercased) label into two roughly balanced lines at a word break.
function splitTwoLines(text) {
  const words = text.split(/\s+/);
  if (words.length < 2) return [text, ''];
  let best = [text, ''], bestScore = Infinity;
  for (let i = 1; i < words.length; i++) {
    const a = words.slice(0, i).join(' '), b = words.slice(i).join(' ');
    const score = Math.max(a.length, b.length);
    if (score < bestScore) { bestScore = score; best = [a, b]; }
  }
  return best;
}

// Curved main-category label. Stays on one arc when it fits; otherwise wraps to
// two balanced lines on stacked concentric arcs so long names show in full
// (instead of running off the end of a single arc and being clipped).
export function MainLabel({ cx, cy, r, a1, a2, text }) {
  const upper = text.toUpperCase();
  const mid = (a1 + a2) / 2;
  const isBottom = mid > 0 && mid < Math.PI; // flip on the lower half to read upright
  const pad = 0.04;
  const arcAngle = (a2 - a1) - 2 * pad;
  const CHAR = 6.95; // ~px per char at the 10.5px mono label size
  const slug = text.replace(/[^A-Za-z0-9]/g, '').toLowerCase(); // unique across both wheels

  const arcAt = (rr) => {
    const from = isBottom ? a2 - pad : a1 + pad;
    const to = isBottom ? a1 + pad : a2 - pad;
    const sweep = isBottom ? 0 : 1;
    const x1 = cx + rr * Math.cos(from), y1 = cy + rr * Math.sin(from);
    const x2 = cx + rr * Math.cos(to), y2 = cy + rr * Math.sin(to);
    return `M ${x1} ${y1} A ${rr} ${rr} 0 0 ${sweep} ${x2} ${y2}`;
  };
  const line = (txt, rr, key) => {
    const id = `main-${slug}-${key}`;
    const budget = rr * arcAngle - 4;
    const fit = txt.length * CHAR > budget
      ? { textLength: Math.max(10, budget), lengthAdjust: 'spacingAndGlyphs' }
      : {};
    return (
      <React.Fragment key={key}>
        <defs><path id={id} d={arcAt(rr)} /></defs>
        <text className="wheel-main-label">
          <textPath href={`#${id}`} startOffset="50%" textAnchor="middle" {...fit}>{txt}</textPath>
        </text>
      </React.Fragment>
    );
  };

  if (upper.length * CHAR <= r * arcAngle) {
    return <g pointerEvents="none">{line(upper, r, '1')}</g>;
  }
  const [l1, l2] = splitTwoLines(upper);
  const gap = 11;
  // First line reads on top: outer arc on the top half, inner arc when flipped.
  const r1 = isBottom ? r - gap / 2 : r + gap / 2;
  const r2 = isBottom ? r + gap / 2 : r - gap / 2;
  return <g pointerEvents="none">{line(l1, r1, 'a')}{line(l2, r2, 'b')}</g>;
}
